import { useState, useEffect } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { UserRole } from '@/types/policy';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  designation: string | null;
  roles: string[];
}

interface UserSelectorProps {
  label: string;
  selectedUsers: string[];
  onUsersChange: (userIds: string[]) => void;
  allowedRoles: UserRole[];
}

export const UserSelector = ({ label, selectedUsers, onUsersChange, allowedRoles }: UserSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const { profile } = useAuth();
  
  useEffect(() => {
    fetchTeamMembers();
  }, [profile?.company_id]);

  const fetchTeamMembers = async () => {
    if (!profile?.company_id) return;
    
    try {
      // Fetch profiles in the company
      const { data: profiles } = await supabase
        .from('profiles')
        .select('*')
        .eq('company_id', profile.company_id);

      if (profiles) {
        // Get roles for each profile
        const membersWithRoles = await Promise.all(
          profiles.map(async (p) => {
            const { data: roles } = await supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', p.id);
            
            return {
              id: p.id,
              name: p.name,
              email: p.email,
              designation: p.designation,
              roles: roles?.map(r => r.role) || []
            };
          })
        );
        setTeamMembers(membersWithRoles);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const availableUsers = teamMembers.filter(user => 
    user.roles.some(role => allowedRoles.map(r => r.toLowerCase()).includes(role))
  );
  const selectedUserObjects = teamMembers.filter(user => selectedUsers.includes(user.id));

  const toggleUser = (userId: string) => {
    if (selectedUsers.includes(userId)) {
      onUsersChange(selectedUsers.filter(id => id !== userId));
    } else {
      onUsersChange([...selectedUsers, userId]);
    }
  };

  const removeUser = (userId: string) => {
    onUsersChange(selectedUsers.filter(id => id !== userId));
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {/* Selected users display */}
      {selectedUserObjects.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-background">
          {selectedUserObjects.map((user) => (
            <Badge key={user.id} variant="secondary" className="flex items-center gap-1">
              <span className="text-xs">
                {user.name} ({user.designation})
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => removeUser(user.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* User selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            Select {label.toLowerCase()}...
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
            <CommandList>
            <CommandEmpty>{loading ? 'Loading users...' : 'No users found.'}</CommandEmpty>
              <CommandGroup>
                {availableUsers.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${user.name} ${user.email} ${user.designation}`}
                    onSelect={() => toggleUser(user.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedUsers.includes(user.id) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="font-medium">{user.name}</span>
                      <span className="text-sm text-muted-foreground">
                        {user.designation} • {user.email}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};